/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/Arrowhead_dark_left.svg";
import __asset_1 from "@res/icons/Arrowhead_light_right.svg";
import __asset_2 from "@res/icons/Arrowhead_dark_right.svg";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {DB_Patient} from "@app/app-shared/patient";
import {CardHeader} from "../../widget/CardHeader";
import {FONTS} from "@res/fonts";
import {Card} from "../../widget/Card";
import * as React from "react";
import {useContext} from "react";
import {graphBodyStyle, graphScroll} from "../graphs/GraphPanelWrapper";
import {OnRemindersActions, RemindersModule} from "@modules/RemindersModule";
import {CalendarEventType, ReminderStatusResponse, StatusType} from "@app/ir-q-app-common/types/calendar_event";
import {icon_tick_gray, icon_tick_green, icon_tick_red} from "../patients/PatientTab";
import dayjs from "../../../utils/dayjs";
import {Reminder} from "@app/app-shared/reminder";
import {Day, Week} from "@intuitionrobotics/ts-common";
import {COLORS} from "@res/colors";
import {hideTooltip, showTooltipAtXY} from "../../widget/appTooltip";
import {GraphUtil} from "../graphs/GraphUtil";
import ContinuousTableGraph, {TableData} from "../../../../app/_to_remove/ContinuousTableGraph";
import {generateHex} from "@intuitionrobotics/ts-common/utils/random-tools";
import {GraphsModule} from "../../../_to_remove/GraphsModule";
import {OrganizationMap, OrganizationsContext} from "../organizations/OrganizationsContext";
import {ContactsContext, ContactsMap} from "../messages/ContactsContext";
import { ORGANIZATION_TYPE } from "@app/ir-q-app-common/types/care-center/organization";

enum GraphIconSentiment {
    NEGATIVE = 1,
    NEUTRAL = 3,
    POSITIVE = 5,
}

type Props = {
    patient: DB_Patient
}

type PropsForClassComponent = {
    patient: DB_Patient;
    organizations: OrganizationMap;
    contacts: ContactsMap
}

type State = {
    endDate: number
}

export const MedicationReminderPanel= (props: Props) => {
    const {organizations} = useContext(OrganizationsContext);
    const {contacts} = useContext(ContactsContext);

    return <MedicationReminderPanelClass patient={props.patient} organizations={organizations} contacts={contacts}/>
}

export class MedicationReminderPanelClass
    extends BaseComponent<PropsForClassComponent, State>
    implements OnRemindersActions {
    constructor(props: PropsForClassComponent) {
        super(props);

        this.state = {
            endDate: GraphsModule.getEndDate()
        }
    }

    __onRemindersQueried = () => this.forceUpdate()

    private reminderStatusesLegend = () => {
        return GraphUtil.renderGraphLegend(
            [
                [
                    <img alt={"tick_green"} src={icon_tick_green} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark("Took meds")
                ],
                [
                    <img alt={"tick_red"} src={icon_tick_red} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark(`Not taken`)
                ],
                [
                    <img alt={"tick_red"} src={icon_tick_gray} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark(`No response`)
                ]
            ]);
    };

    private readonly item_height = 80;

    render() {
        if (!this.props.contacts || !Object.keys(this.props.contacts).length)
            return <div></div>

        const reminders = this.getReminders();
        if (!reminders.length)
            return <div></div>

        const reminderStatuses = RemindersModule.getReminderStatuses(this.props.patient._id);
        if (!reminderStatuses || !Object.keys(reminderStatuses).length) {
            return <div></div>
        }

        const remindersToDisplay: Reminder[] = this.filterAndSortReminders(reminders, reminderStatuses);

        const graphHeight = this.item_height * remindersToDisplay.length;

        return (<Card>
            <CardHeader>
                <div className={"ll_h_c"}>
                    {FONTS.medium_18_graysdark('Medication Reminders', {whiteSpace: "nowrap"})}
                    {this.renderScrollBetweenDates()}
                </div>
                {this.reminderStatusesLegend()}
            </CardHeader>
            <div className={graphBodyStyle}
                // style={{maxHeight: 400, overflowY: "auto"}}
            >
                <div className={"ll_v_s"} style={{width: 300, height: graphHeight, borderRight: `1px solid ${COLORS.neutral_gray_1}`}}>
                    {
                        // we reverse the reminders array in order to match the icons in graph
                        remindersToDisplay.map((reminder, index) => {
                            const nextReminder = remindersToDisplay[index + 1];
                            const prevReminder = remindersToDisplay[index - 1];
                            const showUpperDivider = index !== 0 && !this.isSameInstance(prevReminder, reminder);
                            const showReminderSubject = index === 0 || !this.isSameInstance(prevReminder, reminder)
                            const showLowerDivider = index === remindersToDisplay.length - 1 && !this.isSameInstance(nextReminder, reminder);
                            return <div key={index}
                                        style={{display: "flex", flexDirection: "column", paddingLeft: 10, height: this.item_height}}>
                                {
                                    showUpperDivider &&
                                    <div
                                        style={{backgroundColor: "#a6aab2", height: 0.7, verticalAlign: 'top'}}
                                    />
                                }
                                <div
                                    style={{display: "flex", flexDirection: "column", paddingTop: 10, height: this.item_height, width: 300}}>
                                    <div style={{overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", marginRight: 10, alignItems: 'center'}}
                                         onMouseEnter={(e) => {
                                             const rect = e.currentTarget.getBoundingClientRect();
                                             showTooltipAtXY(
                                                 FONTS.semi_bold_14(reminder.subject!),
                                                 rect.x - 10, rect.y - 30
                                             );
                                         }}
                                         onMouseLeave={hideTooltip}>
                                        {
                                            showReminderSubject &&
                                            FONTS.semi_bold_14(reminder.subject!,
                                                {overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", textAlign: 'center'})
                                        }
                                    </div>
                                    {FONTS.reg_14_gray(`${this.renderRecurrence(reminder)} at ${dayjs(reminder.startTime).format('h:mmA')}`)}
                                    <div style={{marginBottom: 3}}>
                                        {this.renderSetBy(reminder)}
                                    </div>
                                </div>
                                {
                                    showLowerDivider &&
                                    <div
                                        style={{backgroundColor: "#a6aab2", height: 0.4, verticalAlign: 'bottom'}}
                                    />
                                }
                            </div>
                        })
                    }
                </div>
                <div className={graphScroll} style={{
                    width: "100%",
                    overflow: "auto",
                    position: "relative",
                    display: "flex",
                    flexDirection: "row-reverse"
                }}>
                    {this.reminderStatusesGraph(graphHeight)}
                </div>

            </div>
        </Card>)
    }

    renderSetBy = (reminder: Reminder) => {
        if (reminder.metadata?.reminderType === CalendarEventType.MEDICATION_REMINDER)
            return FONTS.reg_14_gray("Set by the user");

        if (this.reminderSetByCc(reminder))
            return FONTS.reg_14_gray("Set by the Care Manager");


        return FONTS.reg_14_gray(`Set by ${reminder.metadata?.contactName}, Caregiver`);
    }

    reminderSetByCc = (reminder: Reminder) => {
        const organizationData = RemindersModule.getPatientsOrganizationAndContacts(this.props.contacts, this.props.organizations[this.props.patient.organizationId]);
        if (!organizationData)
            return;

        return (reminder.metadata?.reminderType === CalendarEventType.SUGGESTED_MEDICATION_REMINDER) &&
            ((organizationData.contacts.orgContact?._id === reminder.metadata?.contactId) || (organizationData.contacts.customerServiceContact?._id === reminder.metadata?.contactId));
    }

    private isSameInstance(first?: Reminder, second?: Reminder) {
        if (!first?.metadata?.sharedReminderInstance || !second)
            return false;
        if (first.metadata?.sharedReminderInstance !== second.metadata?.sharedReminderInstance)
            return false;
        return dayjs(first.startTime).isSame(second.startTime, "day") && first.subject === second.subject;
    }

    private renderScrollBetweenDates() {
        const date = `${dayjs(this.state.endDate - 2 * Week + Day).format("MMM DD")} - ${dayjs(this.state.endDate).format("MMM DD")}`;
        const isDisabled = this.state.endDate + 2 * Week > GraphsModule.getEndDate();
        return <div style={{display: "flex", flexDirection: "row"}}>
            <button style={{marginLeft: 40, backgroundColor: "white", border: "white", display: "flex", alignSelf: "center"}}
                    onClick={() => this.setState(s => ({endDate: s.endDate - 2 * Week}))}>
                <img src={__asset_0}/>
            </button>

            {FONTS.reg_14(date, {marginLeft: 16, marginRight: 16, alignSelf: "center", display: "flex"})}

            <button style={{backgroundColor: "white", border: "white", display: "flex", alignSelf: "center"}} disabled={isDisabled}
                    onClick={() => this.setState(s => ({endDate: s.endDate + 2 * Week}))}>
                {isDisabled ? <img src={__asset_1}/> : <img src={__asset_2}/>}
            </button>
        </div>;
    }

    renderRecurrence = (reminder: Reminder) => {
        const recurrence = reminder.recurring;
        if (!recurrence) {
            // if the event is not recurring display the date of the one time reminder
            return dayjs(reminder.startTime).format("MM/DD/YYYY ");
        }

        return RemindersModule.generateRecurrenceString(recurrence, false);
    }

    private reminderStatusesGraph = (graphHeight: number) => {
        const data = this.convertReminderStatusData();
        return (<ContinuousTableGraph
            key={generateHex(32)}
            sizing={{height: graphHeight + 60, width: 80 * 15}}
            rowHeight={this.item_height}
            frequency={Day}
            data={data}
            viewBox={15}
            endDate={this.state.endDate}
            linesToDraw={this.separateGroupReminders()}
            timeframe={90}/>);
    }

    private getReminders = () => {
        let reminders = RemindersModule.getReminders(this.props.patient._id) || [];
        const organization = this.props.organizations[this.props.patient.organizationId];
        if (organization.type !== ORGANIZATION_TYPE.CLINICAL)
            reminders = RemindersModule.filterOnlyCcReminder(this.props.contacts,
                this.props.organizations[this.props.patient.organizationId], reminders) || [];

        return reminders;
    }

    private separateGroupReminders = () => {
        const reminders = this.getReminders();

        const reminderStatuses = RemindersModule.getReminderStatuses(this.props.patient._id) as ReminderStatusResponse;

        const groupedReminders = this.groupRemindersBySharedId(reminders, reminderStatuses);

        return Object.values(groupedReminders).reduce((sum: number[], _reminders: Reminder[]) => {
            if (sum.length === 0)
                return [_reminders.length];

            sum.push(_reminders.length + sum[sum.length - 1]);
            return sum;
        }, []);
    }

    getStatusIcon = (reminderStatus: StatusType) => {
        let val;
        switch (reminderStatus) {
            case StatusType.DISMISSED:
                val = GraphIconSentiment.POSITIVE;
                break;
            case StatusType.TIMEOUT:
                val = GraphIconSentiment.NEUTRAL;
                break;
            case StatusType.SNOOZE:
                val = GraphIconSentiment.NEUTRAL;
                break;
            case StatusType.TRIGGERED:
                val = GraphIconSentiment.NEUTRAL;
                break;
            case StatusType.REJECTED:
                val = GraphIconSentiment.NEGATIVE;
                break;
            default:
                val = GraphIconSentiment.NEUTRAL;
                break;
        }
        return GraphUtil.resolveIconOnScale(val, icon_tick_green, icon_tick_gray, icon_tick_red, "", false);
    }

    private convertReminderStatusData = () => {
        let reminders = this.getReminders();
        if (!reminders.length)
            return [];

        const reminderStatuses = RemindersModule.getReminderStatuses(this.props.patient._id);
        if (!reminderStatuses)
            return [];

        reminders = reminders.filter((reminder) => reminderStatuses.hasOwnProperty(reminder.id))

        const checkInTableData: TableData[] = [];

        const sortedGroups = this.filterAndSortReminders(reminders, reminderStatuses, false, true);

        sortedGroups.forEach((reminder, index) => {
            const statuses = reminderStatuses[reminder.id].sort(item => item.timestamp);
            if (!statuses)
                return;
            statuses.forEach((status) => {
                checkInTableData.push({
                    label: "",
                    data: {x: dayjs(status.timestamp).startOf("day").valueOf(), y: index, _id: reminder.id.toString()},
                    icon: this.getStatusIcon(status.reminder_status || status.reminderStatus),
                    groupId: reminder.metadata?.sharedReminderInstance || generateHex(32)
                })
            })
        })

        return checkInTableData;
    }

    // reminders have to be sorted in descending timestamp, exactly as they come from the api call
    filterAndSortReminders = (reminders: Reminder[], reminderStatuses: ReminderStatusResponse, reverse = true, reverseGroups: boolean = false) => {
        const groupedReminders = this.groupRemindersBySharedId(reminders, reminderStatuses);

        const toReturn = Object.values(groupedReminders).sort(item => item[0].startTime);

        if (reverseGroups)
            toReturn.forEach(item => item.reverse());

        if (reverse)
            return toReturn.reverse().flat();

        return toReturn.flat();
    }

    private groupRemindersBySharedId(reminders: Reminder[], reminderStatuses: ReminderStatusResponse) {
        const startDate = dayjs(this.state.endDate - 2 * Week + Day).valueOf();

        const validIds: string[] = [];
        for (const reminderStatusId of Object.keys(reminderStatuses)) {
            const statuses = reminderStatuses[reminderStatusId];
            if (statuses.some(status => status.timestamp >= startDate)) {
                validIds.push(reminderStatusId)
            }

        }

        return reminders.reduce((carry: { [key: string]: Reminder[] }, reminder: Reminder) => {
            if (!reminder.subject || !validIds.includes(reminder.id.toString())) {
                return carry;
            }

            const metadata = reminder?.metadata;

            if (!metadata?.sharedReminderInstance) {
                carry[generateHex(32)] = [reminder];
                return carry;
            }

            const groupIdentifier = metadata.sharedReminderInstance + reminder.subject + dayjs(reminder.startTime).format("YYYY-MM-DD");
            if (!carry[groupIdentifier]) {
                carry[groupIdentifier] = [reminder];
            } else {
                carry[groupIdentifier].push(reminder);
            }

            return carry;
        }, {});
    }
}
